/**
 * Motor "distribuir" — arrastar itens ate cada destinatario ficar igual.
 *
 * E divisao concreta: repartir comida entre os animais ate todos terem a mesma
 * quantidade. A crianca nao calcula nada — ela reparte e olha.
 *
 * resto_permitido: quando true, sobrar item nao impede concluir. E o primeiro
 * contato com "nao dividiu certinho", sem que isso vire erro (D6).
 */

import * as THREE from 'three';
import { BaseEngine } from './BaseEngine.js';

const CORES_DESTINO = [0xffcc80, 0x80deea, 0xc5e1a5];

export class DistribuirEngine extends BaseEngine {
  constructor(ctx) {
    super(ctx);
    this.destinos = [];
    this.itens = [];
    this.grupo = null;
  }

  async iniciar(tarefa, banda) {
    await super.iniciar(tarefa, banda);

    const payload = tarefa.bandas[String(banda)];
    if (!payload) {
      throw new Error(`FAZENDA: a tarefa "${tarefa.id}" nao tem payload para a banda ${banda}.`);
    }

    this.numeroDeItens = payload.itens;
    this.numeroDeDestinos = payload.destinatarios;
    this.restoPermitido = payload.resto_permitido === true;

    if (!this.restoPermitido && this.numeroDeItens % this.numeroDeDestinos !== 0) {
      throw new Error(
        `FAZENDA: a tarefa "${tarefa.id}" na banda ${banda} tem ${this.numeroDeItens} itens para ` +
          `${this.numeroDeDestinos} destinatarios e resto_permitido e false. ` +
          'Ou a divisao tem que ser exata, ou resto_permitido tem que ser true.'
      );
    }

    this.montar();
    console.info(
      `FAZENDA: tarefa "${tarefa.id}" iniciada — banda ${banda}, ` +
        `${this.numeroDeItens} itens para ${this.numeroDeDestinos} destinatarios.`
    );
  }

  montar() {
    const { fazenda, sceneManager, input } = this.ctx;

    this.grupo = new THREE.Group();
    this.grupo.name = 'tarefa-distribuir';
    const base = fazenda.posicaoDaAncora(this.tarefa.ancora);
    const largura = (this.numeroDeDestinos - 1) * 2.6;

    for (let i = 0; i < this.numeroDeDestinos; i++) {
      const comedouro = new THREE.Mesh(
        new THREE.CylinderGeometry(1.0, 0.8, 0.6, 8),
        new THREE.MeshStandardMaterial({
          color: CORES_DESTINO[i % CORES_DESTINO.length],
          roughness: 1,
          flatShading: true,
        })
      );
      comedouro.position.set(base.x - largura / 2 + i * 2.6, base.y + 0.4, base.z + 2.8);
      comedouro.userData.recebidos = 0;
      this.grupo.add(comedouro);
      this.destinos.push(comedouro);
    }

    for (let i = 0; i < this.numeroDeItens; i++) {
      const item = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.28, 0),
        new THREE.MeshStandardMaterial({ color: 0xf9d71c, roughness: 0.9, flatShading: true })
      );
      const porLinha = 5;
      item.position.set(
        base.x - 2.4 + (i % porLinha) * 1.2,
        base.y + 0.4,
        base.z - 1.4 + Math.floor(i / porLinha) * 1.1
      );
      item.userData.entregueEm = null;

      this.grupo.add(item);
      this.itens.push(item);

      input.registrarArrastavel(item, {
        destinos: () => this.destinos,
        aoSoltar: (objeto, destino) => this.aoSoltarItem(objeto, destino),
      });
    }

    sceneManager.adicionar(this.grupo);
    input.conferirTamanhoDeAlvo(this.destinos[0], `comedouro da tarefa ${this.tarefa.id}`);
  }

  aoSoltarItem(item, destino) {
    const { audio } = this.ctx;

    if (item.userData.entregueEm) {
      item.userData.entregueEm.userData.recebidos -= 1;
      item.userData.entregueEm = null;
    }

    if (!destino) {
      audio.tocarTentarDeNovo(); // D6 — soltou no vazio, sem drama
      return;
    }

    destino.userData.recebidos += 1;
    item.userData.entregueEm = destino;

    const n = destino.userData.recebidos - 1;
    item.position.set(
      destino.position.x + ((n % 3) - 1) * 0.4,
      destino.position.y + 0.5 + Math.floor(n / 3) * 0.35,
      destino.position.z
    );

    audio.tocarToque(n);
    if (this.concluiu()) audio.tocarSucesso();
  }

  /** Todos os destinatarios com a mesma quantidade, e nada mais sobrando. */
  concluiu() {
    const contagens = this.destinos.map((d) => d.userData.recebidos);
    const iguais = contagens.every((c) => c === contagens[0] && c > 0);
    if (!iguais) return false;

    const sobrando = this.itens.filter((i) => !i.userData.entregueEm).length;
    return this.restoPermitido ? sobrando < this.numeroDeDestinos : sobrando === 0;
  }

  atualizar(dt) {
    for (const item of this.itens) {
      if (!item.userData.entregueEm) item.rotation.y += dt * 1.2;
    }
  }

  async concluir() {
    return super.concluir({ concluiu: this.concluiu() });
  }

  descartar() {
    const { input, sceneManager } = this.ctx;
    for (const i of this.itens) input.desregistrar(i);
    if (this.grupo) {
      sceneManager.remover(this.grupo);
      this.grupo.traverse((o) => { o.geometry?.dispose(); o.material?.dispose(); });
    }
    this.destinos = [];
    this.itens = [];
    this.grupo = null;
    super.descartar();
  }
}
